/**
 * English UI copy for the Embed Studio, share menu, and widget chrome.
 *
 * The studio is an English-only page type (same publication gate as the
 * educator toolkit). Widget chrome stays English so the generator inside can
 * keep using the existing localized tool dictionary.
 */

export const EMBED_COPY = {
  studio: {
    title: "Embed Studio",
    metaTitle: "Embed a Citation Generator in Your LMS or CMS",
    metaDescription:
      "Build a branded iframe for any CitationEasy generator or style and paste it into Canvas, Moodle, Blackboard, LibGuides, WordPress, or any HTML page.",
    eyebrow: "LMS · CMS · LibGuides",
    heading: "Embed a citation generator",
    lead: "Pick a generator, choose the chrome, and copy an iframe that works in Canvas, Moodle, Blackboard, Schoology, LibGuides, WordPress, and any page that accepts HTML. Students never create an account. Bibliography data stays in their browser.",
    breadcrumb: "Embed Studio",
    targetHeading: "What to embed",
    appearanceHeading: "Appearance",
    sizeHeading: "Size",
    snippetHeading: "Embed code",
    previewHeading: "Live preview",
    platformsHeading: "Paste it into your platform",
    kindGenerator: "Universal generator",
    kindStyle: "Style generator",
    kindTool: "Source or writing tool",
    kindBibliography: "Bibliography Studio",
    styleSearchLabel: "Find a citation style",
    styleSearchPlaceholder: "APA, Nature, Vancouver, IEEE…",
    toolSearchLabel: "Find a tool",
    toolSearchPlaceholder: "Book, DOI, word counter…",
    themeLabel: "Theme",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "Match the reader",
    chromeLabel: "Chrome",
    chromeBranded: "Branded",
    chromeMinimal: "Minimal",
    chromeBare: "Bare",
    densityLabel: "Density",
    densityCompact: "Compact",
    densityComfortable: "Comfortable",
    densitySpacious: "Spacious",
    radiusLabel: "Corners",
    accentLabel: "Accent",
    heightLabel: "Height (px)",
    widthLabel: "Width",
    widthFull: "Full width",
    widthFixed: "Fixed",
    showTitle: "Show title",
    showFooter: "Show CitationEasy footer",
    showBorder: "Show border",
    autoResize: "Auto-resize height",
    prefillLabel: "Prefill source (optional)",
    prefillPlaceholder: "DOI, URL, ISBN, PMID, or arXiv ID",
    copyHtml: "Copy HTML",
    copied: "Copied",
    copyFailed: "Couldn’t copy. Select the snippet and copy it manually.",
    openFullPage: "Open the full page",
    reset: "Reset to defaults",
    htmlLabel: "HTML iframe",
    oembedLabel: "oEmbed URL",
    scriptNote:
      "The optional embed.js script listens for a resize message and updates the iframe height. Omit it if your LMS strips scripts — the iframe still works at the height you set.",
    showAttribution: "Include the credit line",
    attributionNote:
      "The credit line is the only part of an embed that lives in your page rather than inside the frame, so it is what tells a reader — and a search engine — where the generator came from. Leaving it on is appreciated and never required.",
  },
  menu: {
    share: "Share",
    shareMenu: "Share and embed",
    copyLink: "Copy page link",
    linkCopied: "Link copied",
    embed: "Embed this generator",
    embedDescription:
      "Get an iframe for Canvas, Moodle, LibGuides, or any CMS.",
    openStudio: "Open Embed Studio",
    copyEmbed: "Copy default embed code",
    embedCopied: "Embed code copied",
    customize: "Customize appearance",
    close: "Close",
  },
  widget: {
    brand: "CitationEasy",
    openInNewTab: "Open full page",
    footer: "Free citation generator — no account required",
    unsupported: "This embed target is not available.",
  },
  platforms: {
    canvas: {
      name: "Canvas",
      steps:
        "In the rich content editor, switch to HTML and paste the iframe. Canvas Pages, Modules, and Assignments all accept it. Use a height of 720 or turn on auto-resize.",
    },
    moodle: {
      name: "Moodle",
      steps:
        "Add a Page or Label, open the HTML source view, and paste the iframe. If Moodle strips iframes, ask an admin to allow citationeasy.com in the HTML purifier.",
    },
    blackboard: {
      name: "Blackboard",
      steps:
        "Create a Content Item, choose the HTML editor, and paste the iframe. Mashups that only accept a URL can use the widget address itself.",
    },
    schoology: {
      name: "Schoology",
      steps:
        "Add a Page or Assignment, open the HTML editor, and paste the iframe. Some districts restrict iframes to an allow-list — add citationeasy.com.",
    },
    libguides: {
      name: "LibGuides / Springshare",
      steps:
        "Add a Rich Text/HTML box or a Media/Widget box and paste the iframe. A guide can host several style-specific embeds on one page.",
    },
    wordpress: {
      name: "WordPress",
      steps:
        "Paste the widget URL into a paragraph — WordPress will oEmbed it — or drop the HTML into a Custom HTML block.",
    },
    googleSites: {
      name: "Google Sites",
      steps:
        "Insert → Embed → Embed code, then paste the iframe. Google Classroom itself prefers a link; use Sites or your LMS for the live generator.",
    },
    notion: {
      name: "Notion",
      steps:
        "Type /embed and paste the widget URL. Notion frames the URL directly; appearance query parameters still apply.",
    },
  },
  faqs: [
    {
      question: "What is the credit line under the embed?",
      answer:
        "Copied snippets end with a short line naming the generator and linking to its page on CitationEasy. It sits outside the iframe because an iframe is not a link: without it, nothing in your page says where the tool came from. Uncheck \u201cInclude the credit line\u201d in Embed Studio to leave it out \u2014 the widget works identically either way, and nothing about your embed changes.",
    },
    {
      question: "Can I embed CitationEasy in Canvas, Moodle, or LibGuides?",
      answer:
        "Yes. Open Embed Studio, choose a generator or style, copy the iframe, and paste it into the platform’s HTML editor. Dedicated widget URLs are allowed to be framed; the rest of the site still refuses framing.",
    },
    {
      question: "Do students need an account?",
      answer:
        "No. Citation generation and local bibliographies work without an account. Data stays in the student’s browser unless they create a share link.",
    },
    {
      question: "Can I lock the embed to APA or another style?",
      answer:
        "Yes. Embed a style generator — /embed/widget/style/apa, /mla, /chicago, or any CSL style slug. The style is preselected and locked so the assignment cannot drift.",
    },
    {
      question: "Is the embed accessible and private?",
      answer:
        "The iframe includes a title, keyboard-operable controls, and a text alternative via the full page link. CitationEasy does not receive roster data. Metadata lookups are the same as on the public site and are described in the privacy policy.",
    },
    {
      question: "What if my LMS strips the resize script?",
      answer:
        "The generator still works. Set an explicit height in the studio (720px is the default). Auto-resize is an enhancement, not a requirement.",
    },
  ],
} as const;

export const EMBED_PLATFORM_KEYS = [
  "canvas",
  "moodle",
  "blackboard",
  "schoology",
  "libguides",
  "wordpress",
  "googleSites",
  "notion",
] as const;

export type EmbedPlatformKey = (typeof EMBED_PLATFORM_KEYS)[number];
